import {LibraryManifest} from '../types';
import {LIBRARY_TRAITS} from '../constants';
import {weakMemo} from '@packages/utilities/memo';
import {emptyArray} from '@packages/utilities/fp/utils';
import {Qualified, getPluginName} from '@packages/systems/wfdl-Qualified';

const ACTIONS = ['edit', 'delete', 'rename', 'view'] as const;
type Action = (typeof ACTIONS)[number];
type ActionSet = Set<Action>;

type CapabilityMap = Record<'styleBlocks' | 'variables', ActionSet>;

export interface LibraryCapabilities extends CapabilityMap {
  // used for hiding libraries from the UI
  isHidden: boolean;
}

export type LibrariesCapabilitiesMap = Record<string, LibraryCapabilities>;

const actionSet = (actions: Action[]): ActionSet => new Set(actions);

const VIEW_ONLY_CAPABILITIES: LibraryCapabilities = {
  isHidden: false,
  variables: actionSet(['view']),
  styleBlocks: actionSet([]),
};

const VIEW_AND_EDIT = actionSet(['view', 'edit']);
const HIDDEN_VIEW_AND_EDIT_CAPABILITIES: LibraryCapabilities = {
  isHidden: true,
  variables: VIEW_AND_EDIT,
  styleBlocks: VIEW_AND_EDIT,
};
const VISIBLE_VIEW_AND_EDIT_CAPABILITIES: LibraryCapabilities = {
  isHidden: false,
  variables: VIEW_AND_EDIT,
  styleBlocks: VIEW_AND_EDIT,
};

const getCapabilitiesFromTraits = weakMemo(
  (
    traits: NonNullable<LibraryManifest<string>['traits']>
  ): LibraryCapabilities => {
    if (traits.includes(LIBRARY_TRAITS.THEMEABLE)) {
      const isHidden = traits.includes(LIBRARY_TRAITS.AI_SITE_GEN);
      if (isHidden) {
        return HIDDEN_VIEW_AND_EDIT_CAPABILITIES;
      } else {
        return VISIBLE_VIEW_AND_EDIT_CAPABILITIES;
      }
    }

    return VIEW_ONLY_CAPABILITIES;
  }
);

export const getLibraryCapabilities = (
  library: Partial<Pick<LibraryManifest<string>, 'traits'>>
): LibraryCapabilities => {
  const traits = library.traits ?? emptyArray;
  return getCapabilitiesFromTraits(traits);
};

/**
 * Checks if a block origin has a specified capability, if origin is not
 * provided, it will return true since by default all blocks are visible.
 */
export function hasCapability({
  action,
  librariesCapabilitiesMap = null,
  origin = null,
}: {
  action: Action;
  librariesCapabilitiesMap: LibrariesCapabilitiesMap | null;
  origin: Qualified<string> | null;
}) {
  if (!origin) {
    // if no origin, return true since site-level blocks should have all
    // capabilities:
    return true;
  }

  const pluginName = getPluginName(origin);
  const capabilities = librariesCapabilitiesMap?.[pluginName];
  return capabilities ? capabilities?.styleBlocks.has(action) : false;
}
